import { useMemo } from 'react';

import { Box, Text, Tooltip } from '@chakra-ui/react';

import { Avatar } from '@/components/core';
import { type SuggestionItemGroup } from '@/components/features/Editor';
import { cn } from '@/lib/utils';
import { glassyEffectButton } from '@/styles/variants/glassy';

import { Markdown } from './Markdown';
import { ThirdPartySvg } from './ThirdPartySvg';

export type MessageProps = {
  children: React.ReactNode;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

const Message = ({ children, className, ...props }: MessageProps) => (
  <div className={cn('flex gap-3', className)} {...props}>
    {children}
  </div>
);

export type MessageAvatarProps = {
  src: string;
  alt: string;
  fallback?: string;
  delayMs?: number;
  className?: string;
};

const MessageAvatar = ({
  src,
  alt,
  fallback,
  delayMs,
  className,
}: MessageAvatarProps) => {
  return (
    <Avatar
      src={src}
      alt={alt}
      fallback={fallback}
      delayMs={delayMs}
      size='sm'
      className={cn('shrink-0', className)}
    />
  );
};

const SlashCommand = ({
  children,
  icon,
}: {
  children: string;
  icon?: string;
}) => {
  return (
    <Box
      display='inline-flex'
      bg='rgba(137, 82, 224, 0.2)'
      color='white'
      lineHeight='20px'
      borderRadius={6}
      padding='2px 10px 2px 4px'
    >
      {icon && (
        <ThirdPartySvg src={icon} mt={1} mr={1} height={11} width={11} />
      )}
      <Text as='span'>{children}</Text>
    </Box>
  );
};

export type MessageContentProps = {
  children: React.ReactNode;
  possibleCommands?: SuggestionItemGroup[];
  markdown?: boolean;
  className?: string;
};

const MessageContent = ({
  children,
  markdown = false,
  possibleCommands,
  className,
}: MessageContentProps) => {
  const classNames = cn('p-2 prose break-words whitespace-normal', className);

  const content = useMemo(() => {
    if (markdown || typeof children !== 'string' || !possibleCommands?.length) {
      return children;
    }

    const possibleCommandsText = possibleCommands.flatMap(group => group.items);

    const escapeRegExp = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const commandRegex = new RegExp(
      `(${possibleCommandsText.map(escapeRegExp).join('|')})`,
      'g'
    );

    const parts = children.split(commandRegex);

    const parsedParts = parts.map((part, index) => {
      for (const group of possibleCommands) {
        for (const item of group.items) {
          if (part === item) {
            return (
              <SlashCommand key={index} children={part} icon={group.icon} />
            );
          }
        }
      }

      return part;
    });

    return <>{parsedParts}</>;
  }, [markdown, children, possibleCommands]);

  return markdown ? (
    <Box className={classNames} sx={glassyEffectButton}>
      <Markdown>{children as string}</Markdown>
    </Box>
  ) : (
    <Box className={classNames} lineHeight='24px' sx={glassyEffectButton}>
      {content}
    </Box>
  );
};

export type MessageActionsProps = {
  children: React.ReactNode;
  className?: string;
} & React.HTMLProps<HTMLDivElement>;

const MessageActions = ({
  children,
  className,
  ...props
}: MessageActionsProps) => (
  <div
    className={cn('text-muted-foreground flex items-center gap-2', className)}
    {...props}
  >
    {children}
  </div>
);

export type MessageActionProps = {
  className?: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
} & React.ComponentProps<typeof Tooltip>;

const MessageAction = ({
  tooltip,
  children,
  placement = 'top',
  ...props
}: MessageActionProps) => {
  return (
    <Tooltip label={tooltip} placement={placement} {...props}>
      {children}
    </Tooltip>
  );
};

export {
  Message,
  MessageAvatar,
  MessageContent,
  MessageActions,
  MessageAction,
};
